import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { listUploads, unlockUploads, resendLeadWebhook } from "@/lib/uploads-gate.functions";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/uploads")({
  head: () => ({
    ...pageSeo({
      path: "/uploads",
      title: "Customer Uploads | CRS Energy",
      description: "Internal dashboard for customer file uploads.",
    }),
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  loader: async () => listUploads(),
  component: UploadsPage,
});

function UploadsPage() {
  const data = Route.useLoaderData();
  const router = useRouter();
  const unlock = useServerFn(unlockUploads);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(false);
    try {
      const { ok } = await unlock({ data: { password } });
      if (ok) {
        await router.invalidate();
      } else {
        setError(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!data.unlocked) {
    return (
      <section className="container-page max-w-md py-16 md:py-24">
        <h1 className="text-2xl font-bold text-foreground mb-2">Customer Uploads</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Enter the password to access customer file uploads.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="uploads-password" className="block text-sm font-medium mb-1.5">
              Password
            </label>
            <input
              id="uploads-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
          {error && (
            <p className="text-sm text-destructive">Incorrect password. Please try again.</p>
          )}
          <button
            type="submit"
            disabled={submitting || !password}
            className="inline-flex items-center justify-center rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold shadow-cta hover:bg-accent/90 transition-colors disabled:opacity-60"
          >
            {submitting ? "Checking..." : "Enter"}
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="container-page py-10 md:py-14">
      <header className="mb-6 flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">Customer Uploads</h1>
        <p className="text-sm text-muted-foreground">
          {data.rows.length} recent submission{data.rows.length === 1 ? "" : "s"}. Links refresh every visit.
        </p>
      </header>
      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 font-semibold">Submitted</th>
              <th className="p-3 font-semibold">Source</th>
              <th className="p-3 font-semibold">Name</th>
              <th className="p-3 font-semibold">Contact</th>
              <th className="p-3 font-semibold">Postcode</th>
              <th className="p-3 font-semibold">Files</th>
              <th className="p-3 font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r: (typeof data.rows)[number]) => (
              <tr key={r.id} className="border-t align-top">
                <td className="p-3 whitespace-nowrap text-muted-foreground">
                  {new Date(r.submitted_at).toLocaleString("en-GB")}
                </td>
                <td className="p-3">
                  <span
                    className={
                      "inline-block rounded-full px-2 py-0.5 text-xs font-medium " +
                      (r.source === "estimate"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800")
                    }
                  >
                    {r.source === "estimate" ? "Estimate" : "Green Home Plans"}
                  </span>
                  {r.traffic_source && (
                    <div className="text-xs font-medium text-foreground mt-1">
                      Came from: {r.traffic_source}
                    </div>
                  )}
                  {r.source_page && (
                    <div className="text-xs text-muted-foreground mt-1">{r.source_page}</div>
                  )}
                </td>
                <td className="p-3 font-medium">{r.name ?? "-"}</td>
                <td className="p-3">
                  {r.email && <div>{r.email}</div>}
                  {r.phone && <div className="text-muted-foreground">{r.phone}</div>}
                </td>
                <td className="p-3 whitespace-nowrap">{r.postcode ?? "-"}</td>
                <td className="p-3">
                  {r.files.length === 0 ? (
                    <span className="text-muted-foreground">-</span>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {r.files.map((f: (typeof r.files)[number]) => (
                        <a
                          key={f.url}
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-accent hover:underline"
                        >
                          {f.label}
                        </a>
                      ))}
                    </div>
                  )}
                </td>
                <td className="p-3 text-muted-foreground max-w-xs">
                  {r.notes ?? "-"}
                  <ResendCell id={r.id} delivery={r.delivery ?? null} />
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted-foreground">
                  No submissions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ResendCell({ id, delivery }: { id: string; delivery: "sent" | "failed" | null }) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const shown = state === "sent" ? "sent" : state === "failed" ? "failed" : delivery;
  return (
    <div className="mt-2 flex items-center gap-2 text-xs">
      {shown === "sent" && <span className="text-foreground">Sent To Zapier</span>}
      {shown === "failed" && <span className="font-semibold text-destructive">Not Sent To Zapier</span>}
      <button
        type="button"
        disabled={state === "sending"}
        onClick={async () => {
          setState("sending");
          const res = await resendLeadWebhook({ data: { ids: [id] } }).catch(() => null);
          setState(res?.results?.[0]?.ok ? "sent" : "failed");
        }}
        className="rounded border px-2 py-0.5 font-medium hover:bg-muted disabled:opacity-50"
      >
        {state === "sending" ? "Sending..." : "Resend"}
      </button>
    </div>
  );
}
